import { sanitizeMemeText } from './memeCaptions';
import type { MemeContext, MemeFormatId, MemeSizeId } from './memeTypes';
import { MEME_SIZES } from './memeTypes';

const BRAND = 'empiremaps.live';
const ORANGE = '#f97316';
const INK = '#0b1120';
const PAPER = '#f4efe6';
const PAPER_INK = '#1c1917';
const MUTED = '#94a3b8';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    loadImage(url)
      .then((img) => {
        URL.revokeObjectURL(url);
        resolve(img);
      })
      .catch((err) => {
        URL.revokeObjectURL(url);
        reject(err);
      });
  });
}

async function tryLogo(url: string | null | undefined): Promise<HTMLImageElement | null> {
  if (!url) return null;
  try {
    return await loadImage(url);
  } catch {
    return null;
  }
}

function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length >= maxLines) break;
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (words.length && lines.length === maxLines) {
    const last = lines[maxLines - 1]!;
    if (ctx.measureText(last).width > maxWidth * 0.98) {
      lines[maxLines - 1] = `${last.replace(/\s+\S+$/, '')}…`;
    }
  }
  return lines;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawBrandFooter(ctx: CanvasRenderingContext2D, w: number, h: number, pad: number) {
  ctx.fillStyle = MUTED;
  ctx.font = `700 ${Math.round(pad * 0.55)}px Teko, system-ui, sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText(BRAND, w - pad, h - pad * 0.55);
  ctx.textAlign = 'left';
}

function stampHeader(
  ctx: CanvasRenderingContext2D,
  title: string,
  accent: string,
  x: number,
  y: number,
  maxW: number
) {
  ctx.fillStyle = accent;
  ctx.font = `800 ${Math.round(maxW * 0.045)}px Teko, "Barlow Condensed", Impact, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(title.toUpperCase(), x, y);
  const tw = ctx.measureText(title.toUpperCase()).width;
  ctx.fillRect(x, y + Math.round(maxW * 0.05), Math.min(tw, maxW * 0.55), 4);
}

async function drawLogoBadge(
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement | null,
  color: string,
  cx: number,
  cy: number,
  size: number
) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = color || '#334155';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 2;
  ctx.stroke();
  if (logo) {
    const s = size * 0.72;
    ctx.drawImage(logo, cx - s / 2, cy - s / 2, s, s);
  }
  ctx.restore();
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/png');
  });
}

async function ensureFonts() {
  try {
    await Promise.all([
      document.fonts.load('800 48px Teko'),
      document.fonts.load('600 24px Teko'),
    ]);
  } catch {
    /* system fallbacks */
  }
}

type LayoutArgs = {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  meme: MemeContext;
  caption: string;
  mapImg: HTMLImageElement;
  winnerLogo: HTMLImageElement | null;
  loserLogo: HTMLImageElement | null;
  teamLogo: HTMLImageElement | null;
};

function drawMapPanel(
  ctx: CanvasRenderingContext2D,
  mapImg: HTMLImageElement,
  x: number,
  y: number,
  rw: number,
  rh: number
) {
  ctx.save();
  roundRect(ctx, x, y, rw, rh, 8);
  ctx.clip();
  const scale = Math.max(rw / mapImg.width, rh / mapImg.height);
  const dw = mapImg.width * scale;
  const dh = mapImg.height * scale;
  ctx.drawImage(mapImg, x + (rw - dw) / 2, y + (rh - dh) / 2, dw, dh);
  ctx.restore();
  ctx.strokeStyle = 'rgba(249,115,22,0.45)';
  ctx.lineWidth = 2;
  roundRect(ctx, x, y, rw, rh, 8);
  ctx.stroke();
}

function drawCaptionBlock(
  ctx: CanvasRenderingContext2D,
  caption: string,
  x: number,
  y: number,
  maxW: number,
  color: string,
  fontPx: number,
  maxLines: number
) {
  ctx.fillStyle = color;
  ctx.font = `600 ${fontPx}px "Courier New", ui-monospace, monospace`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const lines = wrapLines(ctx, caption, maxW, maxLines);
  let yy = y;
  for (const line of lines) {
    ctx.fillText(line, x, yy);
    yy += fontPx * 1.35;
  }
  return yy;
}

async function layoutSurrender(a: LayoutArgs) {
  const { ctx, w, h, meme, caption, winnerLogo, loserLogo } = a;
  const pad = Math.round(w * 0.05);
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, w, h);
  // Document margin rule
  ctx.strokeStyle = 'rgba(28,25,23,0.2)';
  ctx.lineWidth = 2;
  ctx.strokeRect(pad * 0.55, pad * 0.55, w - pad * 1.1, h - pad * 1.1);

  stampHeader(ctx, 'Terms of Surrender', '#b45309', pad, pad, w);
  ctx.fillStyle = PAPER_INK;
  ctx.font = `700 ${Math.round(w * 0.028)}px Teko, system-ui, sans-serif`;
  ctx.fillText('EMPIRE MAPS · OFFICIAL FILING', pad, pad + Math.round(w * 0.06));

  const logoR = Math.round(w * 0.09);
  await drawLogoBadge(
    ctx,
    winnerLogo,
    meme.winner?.color || ORANGE,
    pad + logoR,
    pad + Math.round(h * 0.28),
    logoR * 2
  );
  await drawLogoBadge(
    ctx,
    loserLogo,
    meme.loser?.color || '#64748b',
    w - pad - logoR,
    pad + Math.round(h * 0.28),
    logoR * 2
  );

  ctx.fillStyle = PAPER_INK;
  ctx.font = `800 ${Math.round(w * 0.055)}px Teko, Impact, sans-serif`;
  ctx.textAlign = 'center';
  const midY = pad + Math.round(h * 0.22);
  ctx.fillText(
    `${meme.winner?.short || 'WINNER'}  ›  ${meme.loser?.short || 'LOSER'}`,
    w / 2,
    midY
  );
  ctx.font = `700 ${Math.round(w * 0.032)}px Teko, system-ui, sans-serif`;
  ctx.fillStyle = '#78716c';
  const score =
    meme.winnerScore != null && meme.loserScore != null
      ? `${meme.winnerScore}–${meme.loserScore}`
      : '';
  const meta = [
    meme.countyName ? `${meme.countyName}${meme.stateName ? `, ${meme.stateName}` : ''}` : null,
    meme.countiesFlipped != null ? `${meme.countiesFlipped.toLocaleString()} counties` : null,
    meme.conquestDate || meme.date,
    score || null,
  ]
    .filter(Boolean)
    .join('  ·  ');
  ctx.fillText(meta, w / 2, midY + Math.round(w * 0.07));

  ctx.textAlign = 'left';
  drawCaptionBlock(
    ctx,
    caption,
    pad,
    Math.round(h * 0.55),
    w - pad * 2,
    PAPER_INK,
    Math.round(w * 0.028),
    4
  );

  ctx.fillStyle = '#b45309';
  ctx.font = `800 ${Math.round(w * 0.022)}px Teko, system-ui, sans-serif`;
  ctx.fillText('SIGNED UNDER PROTEST — SEE MAP FOR BOUNDARIES', pad, h - pad * 1.6);
  ctx.fillStyle = '#a8a29e';
  ctx.font = `700 ${Math.round(pad * 0.5)}px Teko, system-ui, sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillText(BRAND, w - pad, h - pad * 0.7);
}

async function layoutConscription(a: LayoutArgs) {
  const { ctx, w, h, meme, caption, teamLogo, mapImg } = a;
  const pad = Math.round(w * 0.045);
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, w, h);
  stampHeader(ctx, 'Conscription Notice', ORANGE, pad, pad, w);

  const mapH = Math.round(h * 0.42);
  drawMapPanel(ctx, mapImg, pad, pad + Math.round(h * 0.12), w - pad * 2, mapH);

  await drawLogoBadge(
    ctx,
    teamLogo,
    meme.team?.color || ORANGE,
    pad + Math.round(w * 0.08),
    pad + Math.round(h * 0.12) + mapH + Math.round(h * 0.1),
    Math.round(w * 0.14)
  );

  ctx.fillStyle = '#f8fafc';
  ctx.font = `800 ${Math.round(w * 0.048)}px Teko, Impact, sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText(
    (meme.team?.name || 'YOUR EMPIRE').toUpperCase(),
    pad + Math.round(w * 0.18),
    pad + Math.round(h * 0.12) + mapH + Math.round(h * 0.06)
  );
  ctx.fillStyle = MUTED;
  ctx.font = `700 ${Math.round(w * 0.024)}px Teko, system-ui, sans-serif`;
  ctx.fillText(
    'VOLUNTEER ROLLS OPEN · ENLIST FREE',
    pad + Math.round(w * 0.18),
    pad + Math.round(h * 0.12) + mapH + Math.round(h * 0.12)
  );

  drawCaptionBlock(
    ctx,
    caption,
    pad,
    Math.round(h * 0.78),
    w - pad * 2,
    '#e2e8f0',
    Math.round(w * 0.026),
    3
  );
  drawBrandFooter(ctx, w, h, pad);
}

async function layoutOperation(a: LayoutArgs) {
  const { ctx, w, h, meme, caption, mapImg, winnerLogo, loserLogo } = a;
  const pad = Math.round(w * 0.04);
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, w, h);

  const title = `OPERATION ${meme.operationName || 'LANDFALL'}`;
  stampHeader(ctx, title, ORANGE, pad, pad * 0.8, w);

  const mapY = pad + Math.round(h * 0.1);
  const mapH = Math.round(h * 0.58);
  drawMapPanel(ctx, mapImg, pad, mapY, w - pad * 2, mapH);

  // Attack arrow overlay (abstract — no weapons)
  ctx.save();
  ctx.strokeStyle = ORANGE;
  ctx.fillStyle = ORANGE;
  ctx.lineWidth = 5;
  const ax0 = pad + Math.round((w - pad * 2) * 0.22);
  const ay0 = mapY + Math.round(mapH * 0.7);
  const ax1 = pad + Math.round((w - pad * 2) * 0.72);
  const ay1 = mapY + Math.round(mapH * 0.28);
  ctx.beginPath();
  ctx.moveTo(ax0, ay0);
  ctx.lineTo(ax1, ay1);
  ctx.stroke();
  const ang = Math.atan2(ay1 - ay0, ax1 - ax0);
  ctx.beginPath();
  ctx.moveTo(ax1, ay1);
  ctx.lineTo(ax1 - 18 * Math.cos(ang - 0.4), ay1 - 18 * Math.sin(ang - 0.4));
  ctx.lineTo(ax1 - 18 * Math.cos(ang + 0.4), ay1 - 18 * Math.sin(ang + 0.4));
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  await drawLogoBadge(
    ctx,
    winnerLogo || a.teamLogo,
    meme.winner?.color || meme.team?.color || ORANGE,
    pad + Math.round(w * 0.07),
    mapY + mapH + Math.round(h * 0.09),
    Math.round(w * 0.11)
  );
  await drawLogoBadge(
    ctx,
    loserLogo,
    meme.loser?.color || '#64748b',
    w - pad - Math.round(w * 0.07),
    mapY + mapH + Math.round(h * 0.09),
    Math.round(w * 0.11)
  );

  drawCaptionBlock(
    ctx,
    caption,
    pad + Math.round(w * 0.14),
    mapY + mapH + Math.round(h * 0.04),
    w - pad * 2 - Math.round(w * 0.28),
    '#f8fafc',
    Math.round(w * 0.025),
    3
  );
  drawBrandFooter(ctx, w, h, pad);
}

async function layoutMia(a: LayoutArgs) {
  const { ctx, w, h, meme, caption, teamLogo, mapImg } = a;
  const pad = Math.round(w * 0.05);
  ctx.fillStyle = '#0a0a0f';
  ctx.fillRect(0, 0, w, h);
  stampHeader(ctx, 'M.I.A. Report', '#ef4444', pad, pad, w);

  drawMapPanel(ctx, mapImg, pad, pad + Math.round(h * 0.12), w - pad * 2, Math.round(h * 0.38));
  // Dim veil
  ctx.fillStyle = 'rgba(10,10,15,0.55)';
  roundRect(ctx, pad, pad + Math.round(h * 0.12), w - pad * 2, Math.round(h * 0.38), 8);
  ctx.fill();

  ctx.fillStyle = '#ef4444';
  ctx.font = `900 ${Math.round(w * 0.12)}px Teko, Impact, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('0', w / 2, Math.round(h * 0.62));
  ctx.fillStyle = MUTED;
  ctx.font = `700 ${Math.round(w * 0.03)}px Teko, system-ui, sans-serif`;
  ctx.fillText('COUNTIES HELD', w / 2, Math.round(h * 0.62) + Math.round(w * 0.05));

  await drawLogoBadge(
    ctx,
    teamLogo,
    meme.team?.color || '#64748b',
    w / 2,
    Math.round(h * 0.42),
    Math.round(w * 0.16)
  );

  ctx.textAlign = 'left';
  drawCaptionBlock(
    ctx,
    caption,
    pad,
    Math.round(h * 0.78),
    w - pad * 2,
    '#e2e8f0',
    Math.round(w * 0.026),
    3
  );
  drawBrandFooter(ctx, w, h, pad);
}

async function layoutDeclaration(a: LayoutArgs) {
  const { ctx, w, h, meme, caption, teamLogo, loserLogo } = a;
  const pad = Math.round(w * 0.05);
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, w, h);
  stampHeader(ctx, 'Declaration of War', '#b45309', pad, pad, w);

  ctx.fillStyle = PAPER_INK;
  ctx.font = `800 ${Math.round(w * 0.06)}px Teko, Impact, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(
    `${meme.team?.short || 'US'}  VS  ${meme.nextGame?.opponentName || meme.rival?.short || 'THEM'}`,
    w / 2,
    Math.round(h * 0.28)
  );

  await drawLogoBadge(
    ctx,
    teamLogo,
    meme.team?.color || ORANGE,
    Math.round(w * 0.28),
    Math.round(h * 0.48),
    Math.round(w * 0.16)
  );
  await drawLogoBadge(
    ctx,
    loserLogo,
    meme.rival?.color || '#78716c',
    Math.round(w * 0.72),
    Math.round(h * 0.48),
    Math.round(w * 0.16)
  );

  ctx.fillStyle = '#78716c';
  ctx.font = `700 ${Math.round(w * 0.028)}px Teko, system-ui, sans-serif`;
  ctx.fillText(
    meme.nextGame?.gameDate
      ? `ENGAGEMENT ${meme.nextGame.gameDate.slice(0, 10)}`
      : 'ENGAGEMENT PENDING',
    w / 2,
    Math.round(h * 0.62)
  );

  ctx.textAlign = 'left';
  drawCaptionBlock(
    ctx,
    caption,
    pad,
    Math.round(h * 0.72),
    w - pad * 2,
    PAPER_INK,
    Math.round(w * 0.026),
    3
  );
  ctx.fillStyle = '#a8a29e';
  ctx.font = `700 ${Math.round(pad * 0.5)}px Teko, system-ui, sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillText(BRAND, w - pad, h - pad * 0.7);
}

async function layoutDispatch(a: LayoutArgs) {
  const { ctx, w, h, meme, caption, mapImg, winnerLogo } = a;
  const pad = Math.round(w * 0.045);
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, w, h);
  // Broadcast bar
  ctx.fillStyle = ORANGE;
  ctx.fillRect(0, 0, w, Math.round(h * 0.08));
  ctx.fillStyle = '#0b1120';
  ctx.font = `900 ${Math.round(h * 0.045)}px Teko, Impact, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('FRONT LINE DISPATCH', pad, Math.round(h * 0.04));
  ctx.textAlign = 'right';
  ctx.fillText('LIVE', w - pad, Math.round(h * 0.04));

  const mapY = Math.round(h * 0.1);
  const mapH = Math.round(h * 0.52);
  drawMapPanel(ctx, mapImg, pad, mapY, w - pad * 2, mapH);

  await drawLogoBadge(
    ctx,
    winnerLogo || a.teamLogo,
    meme.winner?.color || meme.team?.color || ORANGE,
    pad + Math.round(w * 0.07),
    mapY + mapH + Math.round(h * 0.1),
    Math.round(w * 0.12)
  );

  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#f8fafc';
  ctx.font = `800 ${Math.round(w * 0.04)}px Teko, Impact, sans-serif`;
  ctx.fillText(
    (meme.winner?.name || meme.team?.name || 'EMPIRE').toUpperCase(),
    pad + Math.round(w * 0.16),
    mapY + mapH + Math.round(h * 0.05)
  );

  drawCaptionBlock(
    ctx,
    caption,
    pad + Math.round(w * 0.16),
    mapY + mapH + Math.round(h * 0.12),
    w - pad * 2 - Math.round(w * 0.16),
    '#cbd5e1',
    Math.round(w * 0.024),
    3
  );
  drawBrandFooter(ctx, w, h, pad);
}

const LAYOUTS: Record<MemeFormatId, (a: LayoutArgs) => Promise<void>> = {
  surrender: layoutSurrender,
  conscription: layoutConscription,
  operation: layoutOperation,
  mia: layoutMia,
  declaration: layoutDeclaration,
  dispatch: layoutDispatch,
};

export async function renderMemeImage(
  formatId: MemeFormatId,
  meme: MemeContext,
  caption: string,
  sizeId: MemeSizeId
): Promise<Blob> {
  await ensureFonts();
  const { w, h } = MEME_SIZES[sizeId];
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');

  const safeCaption = sanitizeMemeText(caption, 220);
  const [mapImg, winnerLogo, loserLogo, teamLogo] = await Promise.all([
    blobToImage(meme.mapBlob),
    tryLogo(meme.winner?.logoUrl || meme.team?.logoUrl),
    tryLogo(meme.loser?.logoUrl || meme.rival?.logoUrl),
    tryLogo(meme.team?.logoUrl),
  ]);

  await LAYOUTS[formatId]({
    ctx,
    w,
    h,
    meme,
    caption: safeCaption,
    mapImg,
    winnerLogo,
    loserLogo,
    teamLogo,
  });

  return canvasToBlob(canvas);
}
